// biome-ignore lint/suspicious/noExplicitAny: Loose type.
export type ApiProxyFn<T extends any[], U extends Promise<any>> = (...args: T) => U;

// biome-ignore lint/suspicious/noExplicitAny: Loose type.
const state: { [key: string]: Promise<any> | undefined } = {};

/**
 * Serialises call arguments into a dedupe key.
 *
 * Must be faithful: the old `args.map(a => a.toString())` collapsed every object to
 * `[object Object]`, so two different checkout requests deduped to one in-flight promise — and an
 * effect re-run could be handed a promise its predecessor had already aborted. AbortControllers
 * are excluded because two calls differing only by controller are the same request; Files are
 * keyed by identity fields rather than contents.
 */
// biome-ignore lint/suspicious/noExplicitAny: Loose type.
const keyOf = (name: string, args: any[]): string =>
  `${name}:${JSON.stringify(args, (_key, value) => {
    if (value instanceof AbortController || value instanceof AbortSignal) return undefined;
    if (typeof File !== 'undefined' && value instanceof File) {
      return `file:${value.name}:${value.size}:${value.lastModified}`;
    }
    if (typeof value === 'function') return undefined;
    return value;
  })}`;

/**
 * API function wrapper that dedupes concurrent identical calls.
 *
 * Only for reads. A call that creates or mutates server state (checkout sessions, top-ups) must
 * never be handed another call's promise — do not wrap those.
 * @param {string} name - The function name, unique per wrapped function.
 * @param {ApiProxyFn} fn - The function to wrap.
 */
// biome-ignore lint/suspicious/noExplicitAny: Loose type.
const apiProxy = <T extends any[], U extends Promise<any>>(name: string, fn: ApiProxyFn<T, U>) => {
  return (...args: T): U => {
    const h = keyOf(name, args);
    if (state[h]) return state[h] as U;

    state[h] = fn(...args);

    // The cleanup is deliberately detached and given its own catch.
    //
    // `.finally()` returns a NEW promise that settles the same way as the one it is chained to,
    // so on a rejection that derived promise rejects too — and nothing was ever handling it. A
    // caller's `.catch()` only covers the promise it was handed, `state[h]`, so every failed call
    // raised an unhandled rejection however carefully the caller handled it. On an account with
    // no subscription record that is one unhandled 404 per licence check, on a loop.
    void state[h]
      .finally(() => {
        delete state[h];
      })
      .catch(() => {
        // Swallowed here only. The caller still sees the real rejection on `state[h]`.
      });

    return state[h] as U;
  };
};

export default apiProxy;
